import { useCallback, useEffect, useRef, useState } from "react";

const DRAG_THRESHOLD_PX = 4;

/**
 * Press-and-drag horizontal scrolling for the chart.
 *
 * The Sankey is routinely wider than its card, and the scrollbar alone is a
 * poor way to move a chart you are reading. Dragging past the threshold
 * suppresses the click that follows, so a drag never opens a lead list by
 * accident.
 */
export const useDragScroll = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ x: number; scrollLeft: number } | null>(null);
  const movedRef = useRef(false);
  const [canDrag, setCanDrag] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const measure = () =>
      setCanDrag(element.scrollWidth > element.clientWidth + 1);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const element = scrollRef.current;
      if (!element || event.pointerType !== "mouse" || event.button !== 0)
        return;
      if (element.scrollWidth <= element.clientWidth + 1) return;

      dragStart.current = { x: event.clientX, scrollLeft: element.scrollLeft };
      movedRef.current = false;
    },
    []
  );

  useEffect(() => {
    const handleMove = (event: PointerEvent) => {
      const start = dragStart.current;
      const element = scrollRef.current;
      if (!start || !element) return;

      const dx = event.clientX - start.x;
      if (!movedRef.current && Math.abs(dx) > DRAG_THRESHOLD_PX) {
        movedRef.current = true;
        setDragging(true);
      }
      if (movedRef.current) {
        element.scrollLeft = start.scrollLeft - dx;
        event.preventDefault();
      }
    };

    const handleEnd = () => {
      if (!dragStart.current) return;
      dragStart.current = null;
      setDragging(false);
      // Cleared on the next tick so the click this release produces still sees
      // that a drag happened and can suppress itself.
      window.setTimeout(() => {
        movedRef.current = false;
      }, 0);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleEnd);
    window.addEventListener("pointercancel", handleEnd);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleEnd);
      window.removeEventListener("pointercancel", handleEnd);
    };
  }, []);

  const onClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!movedRef.current) return;
      event.stopPropagation();
      event.preventDefault();
    },
    []
  );

  return { scrollRef, canDrag, dragging, onPointerDown, onClickCapture };
};
